import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/core/hooks/useAuth';
import { notifyAvatarUpdate, useAvatar } from '@/core/context/avatar/AvatarContext';
import { getLoginAD } from '@/core/configs/auth/auth.utils';
import { maskLgpd, maskLgpdGenerico } from '@/core/utils/lgpd-mask.util';
import { subscribeLgpd } from '@/services/lgpd.service';
import {
    deleteAvatar,
    uploadAvatar,
} from '@/services/avatar.service';
import { notificarErro, notificarSucesso } from '@/services/toast.service';
import { CotinButton } from '@cotin/biblioteca-componentes-react';
interface UserDropdownProps {
    inlineMode?: boolean;
    onOpenConfiguracoes?: () => void;
}

function fileToDataUrl(file: File): Promise<string | null> {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string) || null);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
    });
}

const ProfileAvatarIcon: React.FC = () => (
    <svg className="profile-avatar-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
            fill="currentColor"
        />
    </svg>
);

const UserDropdown: React.FC<UserDropdownProps> = ({ inlineMode = true, onOpenConfiguracoes }) => {
    const { usuarioSessao, logout } = useAuth();
    const { avatarUrl, setAvatarUrl } = useAvatar();
    const [showAvatarMenu, setShowAvatarMenu] = useState(false);
    const [showAvatarConfirm, setShowAvatarConfirm] = useState(false);
    const [atualizandoAvatar, setAtualizandoAvatar] = useState(false);
    const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);
    const [pendingAvatarPreviewUrl, setPendingAvatarPreviewUrl] = useState<string | null>(null);
    const [, forceLgpdRefresh] = useState(0);
    const avatarMenuRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const unsubscribe = subscribeLgpd(() => {
            forceLgpdRefresh((value) => value + 1);
        });
        return unsubscribe;
    }, []);

    useEffect(() => {
        if (!showAvatarMenu) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (avatarMenuRef.current && !avatarMenuRef.current.contains(event.target as Node)) {
                setShowAvatarMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showAvatarMenu]);

    const exibirNomePerfil = () => {
        const value = usuarioSessao?.nome?.trim();
        if (!value) return '—';
        return maskLgpd(value, 'nome');
    };

    const exibirEmailPerfil = () => {
        const value = usuarioSessao?.email?.trim();
        if (!value) return '—';
        return maskLgpd(value, 'email');
    };

    const clearPendingAvatarState = () => {
        setPendingAvatarFile(null);
        setPendingAvatarPreviewUrl(null);
    };

    const onAvatarLoadError = () => {
        setAvatarUrl(null);
    };

    const editarAvatar = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        fileInputRef.current?.click();
    };

    const excluirAvatar = async () => {
        setShowAvatarMenu(false);
        const loginAD = getLoginAD(usuarioSessao);
        if (!loginAD) {
            notificarErro(['Não foi possível identificar o usuário para remover a foto.']);
            return;
        }

        setAtualizandoAvatar(true);
        try {
            const success = await deleteAvatar(loginAD);
            if (!success) {
                notificarErro(['Não foi possível remover a foto de perfil.']);
                return;
            }
            setAvatarUrl(null);
            notifyAvatarUpdate(null);
            notificarSucesso(['Foto de perfil removida com sucesso.']);
        } catch {
            notificarErro(['Não foi possível remover a foto de perfil.']);
        } finally {
            setAtualizandoAvatar(false);
        }
    };

    const onAvatarFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowAvatarMenu(false);
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
        if (!validTypes.includes(file.type)) {
            notificarErro(['Formato inválido. Apenas JPG, PNG, WEBP e GIF são aceitos.']);
            return;
        }

        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            notificarErro(['Arquivo muito grande. Tamanho máximo: 10MB.']);
            return;
        }

        setPendingAvatarFile(file);
        setPendingAvatarPreviewUrl(await fileToDataUrl(file));
        setShowAvatarConfirm(true);
    };

    const cancelarTrocaAvatar = () => {
        setShowAvatarConfirm(false);
        clearPendingAvatarState();
    };

    const confirmarTrocaAvatar = async () => {
        const file = pendingAvatarFile;
        const loginAD = getLoginAD(usuarioSessao);
        if (!file || !loginAD) {
            notificarErro(['Não foi possível identificar o usuário para trocar a foto.']);
            cancelarTrocaAvatar();
            return;
        }

        setAtualizandoAvatar(true);
        try {
            const uploadedUrl = await uploadAvatar(loginAD, file);
            setAvatarUrl(uploadedUrl);
            notifyAvatarUpdate(uploadedUrl);
            setShowAvatarConfirm(false);
            notificarSucesso(['Foto de perfil atualizada com sucesso.']);
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'Não foi possível salvar a foto de perfil.';
            notificarErro([message]);
        } finally {
            setAtualizandoAvatar(false);
            clearPendingAvatarState();
        }
    };

    const openConfiguracoes = () => {
        onOpenConfiguracoes?.();
        window.setTimeout(() => {
            const closeButton = document.querySelector('.user-sidebar-close') as HTMLButtonElement | null;
            closeButton?.click();
        }, 0);
    };

    const renderInlineContent = () => (
        <>
            <div className="profile-top">
                <div className="profile-avatar-wrapper" ref={avatarMenuRef}>
                    <div className="profile-avatar">
                        {avatarUrl ? (
                            <img
                                src={avatarUrl}
                                onError={onAvatarLoadError}
                                alt="Avatar do usuário"
                                className="profile-avatar-image"
                            />
                        ) : (
                            <ProfileAvatarIcon />
                        )}
                    </div>

                    <button
                        type="button"
                        className="avatar-floating-action"
                        disabled={atualizandoAvatar}
                        onClick={(event) => {
                            event.stopPropagation();
                            setShowAvatarMenu((prev) => !prev);
                        }}
                        aria-label="Opções da foto de perfil"
                        title="Opções da foto de perfil">
                        <span className="material-icons-outlined pencil-outline-icon" aria-hidden="true">
                            edit
                        </span>
                    </button>

                    {showAvatarMenu && (
                        <div className="avatar-photo-menu dropdown-menu show">
                            <button type="button" className="dropdown-item" onClick={editarAvatar}>
                                <span className="material-icons-outlined menu-icon" aria-hidden="true">
                                    photo_camera
                                </span>
                                Alterar foto
                            </button>
                            <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => void excluirAvatar()}
                                disabled={atualizandoAvatar || !avatarUrl}>
                                <span className="material-icons-outlined menu-icon" aria-hidden="true">
                                    delete_outline
                                </span>
                                Remover foto
                            </button>
                        </div>
                    )}

                    {typeof document !== 'undefined' &&
                        createPortal(
                            <input
                                ref={fileInputRef}
                                id="avatarUploadInput"
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                                tabIndex={-1}
                                aria-hidden="true"
                                className="avatar-upload-input"
                                onChange={(event) => void onAvatarFileSelected(event)}
                            />,
                            document.body
                        )}
                </div>
            </div>

            <div className="dropdown-divider" />

            <div className="user-details-section">
                <p className="field-label">Nome completo</p>
                <p className="field-value">{exibirNomePerfil()}</p>
            </div>

            <div className="user-details-section">
                <p className="field-label">E-mail</p>
                <p className="field-value">{exibirEmailPerfil()}</p>
            </div>

            <div className="user-details-section">
                <p className="field-label">Função</p>
                <p className="field-value">{usuarioSessao?.nomeGrupo || '—'}</p>
            </div>

            <div className="user-details-section">
                <p className="field-label">Equipe</p>
                <p className="field-value">{usuarioSessao?.nomeSistema || '—'}</p>
            </div>

            <div className="user-details-section">
                <p className="field-label">Contrato - Item Contrato</p>
                <p className="field-value">{usuarioSessao?.matricula || '058/21 - Item 9'}</p>
            </div>

            <div className="user-details-section">
                <p className="field-label">Lotação</p>
                <p className="field-value">{usuarioSessao?.nomeUnidade || '—'}</p>
            </div>

            <div className="user-details-section">
                <p className="field-label">Tipo de perfil de acesso</p>
                <p className="field-value">{usuarioSessao?.nomeGrupo || 'Consulta'}</p>
            </div>

            <div className="logout-container">
                <button
                    className="logging-gear-btn"
                    type="button"
                    onClick={openConfiguracoes}
                    aria-label="Configurações"
                    title="Configurações">
                    <span className="material-symbols-outlined" aria-hidden="true">
                        settings
                    </span>
                </button>
                <button type="button" className="logout-btn" onClick={() => void logout()}>
                    Deslogar
                </button>
            </div>
        </>
    );

    return (
        <>
            <div className={`user-dropdown ${inlineMode ? 'user-dropdown-inline' : ''}`}>
                {inlineMode ? renderInlineContent() : renderInlineContent()}
            </div>

            {showAvatarConfirm &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div className="modal-overlay avatar-confirm-window" role="presentation">
                        <div
                            className="avatar-confirm-container"
                            onClick={(event) => event.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="avatar-confirm-title">
                            <div className="modal-header">
                                <h4 className="modal-title" id="avatar-confirm-title">
                                    Confirmar nova foto
                                </h4>
                                <button
                                    type="button"
                                    className="confirm-close-btn"
                                    aria-label="Fechar"
                                    onClick={cancelarTrocaAvatar}>
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path
                                            d="M18 6L6 18M6 6l12 12"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            </div>

                            <div className="modal-body">
                                <p className="confirm-avatar-text">
                                    Esta será sua nova foto de perfil. Deseja confirmar?
                                </p>
                                <div className="confirm-avatar-preview">
                                    {pendingAvatarPreviewUrl && (
                                        <img src={pendingAvatarPreviewUrl} alt="Prévia da nova foto de perfil" />
                                    )}
                                </div>
                            </div>

                            <div className="modal-footer">
                                <CotinButton
                                    id="btn-avatar-cancelar"
                                    type="button"
                                    variant="default"
                                    size="medium"
                                    text="Cancelar"
                                    onClick={cancelarTrocaAvatar}
                                    disabled={atualizandoAvatar}
                                />
                                <CotinButton
                                    id="btn-avatar-confirmar"
                                    type="button"
                                    variant="primary"
                                    size="medium"
                                    text={atualizandoAvatar ? 'Salvando...' : 'Confirmar'}
                                    onClick={() => void confirmarTrocaAvatar()}
                                    disabled={atualizandoAvatar}
                                    loading={atualizandoAvatar}
                                />
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default UserDropdown;

export const UserAvatarImage: React.FC<{
    avatarUrl: string | null;
    onError: () => void;
    className?: string;
    size?: number;
}> = ({ avatarUrl, onError, className = 'avatar-image', size = 24 }) => {
    if (avatarUrl) {
        return <img src={avatarUrl} onError={onError} alt="Avatar do usuário" className={className} />;
    }

    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                fill="currentColor"
            />
        </svg>
    );
};

export function useDisplayLoginAD(usuarioSessao: ReturnType<typeof useAuth>['usuarioSessao']): string {
    const [, forceLgpdRefresh] = useState(0);

    useEffect(() => {
        const unsubscribe = subscribeLgpd(() => {
            forceLgpdRefresh((value) => value + 1);
        });
        return unsubscribe;
    }, []);

    const loginAD = getLoginAD(usuarioSessao);
    return loginAD ? maskLgpdGenerico(loginAD) : 'Nome do Usuário';
}
